import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FileText } from "lucide-react";

interface PrescriptionPDFContentProps {
  prescription: {
    id: string;
    patient: {
      full_name: string;
      data_nascimento?: string;
      cpf?: string;
    };
    professional: {
      full_name?: string;
      cpf?: string;
      health_area?: string;
    };
    items: Array<{
      item_type: string;
      prescription: string;
      dosage?: string;
      frequency?: string;
      duration?: string;
      item_date?: string;
    }>;
    administrationPlan?: {
      schedule: Array<{
        item_name: string;
        times: string[];
        days?: string[];
      }>;
      summary: string;
    };
    created_at: string;
  };
}

const itemTypeLabels: Record<string, string> = {
  medication: "Medicamento",
  supplement: "Suplemento",
  vitamin: "Vitamina",
  other: "Outro",
};

export function PrescriptionPDFContent({ prescription }: PrescriptionPDFContentProps) {
  const age = prescription.patient.data_nascimento
    ? Math.floor((new Date().getTime() - new Date(prescription.patient.data_nascimento).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div className="p-8 bg-white text-black" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-teal-600">
        <FileText className="h-8 w-8 text-teal-600" />
        <div>
          <h1 className="text-2xl font-bold text-teal-700">
            Prescrição {prescription.professional.health_area === 'Medicina' ? 'Médica' : 'Nutricional'}
          </h1>
          <p className="text-sm text-gray-600">
            Gerado em {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
          </p>
        </div>
      </div>

      {/* Patient Info */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-teal-700 mb-3">Dados do Paciente</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="font-semibold">Nome:</span> {prescription.patient.full_name}
          </div>
          <div>
            <span className="font-semibold">Idade:</span> {age ? `${age} anos` : 'N/A'}
          </div>
          {prescription.patient.cpf && (
            <div>
              <span className="font-semibold">CPF:</span> {prescription.patient.cpf}
            </div>
          )}
          <div>
            <span className="font-semibold">Data:</span>{' '}
            {format(new Date(prescription.created_at), 'dd/MM/yyyy', { locale: ptBR })}
          </div>
        </div>
      </div>

      {/* Prescription Items */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-teal-700 mb-3">Itens Prescritos</h2>
        <table className="w-full text-sm border border-gray-300">
          <thead className="bg-teal-50">
            <tr>
              <th className="border border-gray-300 p-2 text-left">Tipo</th>
              <th className="border border-gray-300 p-2 text-left">Prescrição</th>
              <th className="border border-gray-300 p-2 text-left">Posologia</th>
              <th className="border border-gray-300 p-2 text-left">Prazo</th>
            </tr>
          </thead>
          <tbody>
            {prescription.items.map((item, index) => (
              <tr key={index}>
                <td className="border border-gray-300 p-2">{itemTypeLabels[item.item_type] || item.item_type}</td>
                <td className="border border-gray-300 p-2 font-semibold">{item.prescription}</td>
                <td className="border border-gray-300 p-2">{item.dosage || '-'}</td>
                <td className="border border-gray-300 p-2">{item.duration || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Administration Plan */}
      {prescription.administrationPlan && prescription.administrationPlan.schedule && Array.isArray(prescription.administrationPlan.schedule) && prescription.administrationPlan.schedule.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-teal-700 mb-3">Plano de Administração</h2>
          {prescription.administrationPlan.summary && (
            <p className="text-sm mb-3 p-3 bg-cyan-50 rounded border border-cyan-200">
              {prescription.administrationPlan.summary}
            </p>
          )}
          <div className="space-y-2 text-sm">
            {prescription.administrationPlan.schedule.map((scheduleItem, index) => (
              <div key={index} className="p-3 border border-gray-300 rounded">
                <p className="font-semibold mb-1">{scheduleItem.item_name}</p>
                <p className="text-gray-700">
                  Horários: {scheduleItem.times.join(', ')}
                  {scheduleItem.days && scheduleItem.days.length > 0 && (
                    <span> | Dias: {scheduleItem.days.join(', ')}</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t-2 border-teal-600 text-xs text-gray-600">
        <div className="grid grid-cols-2 gap-3 mb-3">
          {prescription.professional.full_name && (
            <div>
              <span className="font-semibold">Profissional:</span> {prescription.professional.full_name}
            </div>
          )}
          {prescription.professional.cpf && (
            <div>
              <span className="font-semibold">CPF/CRM/CRN:</span> {prescription.professional.cpf}
            </div>
          )}
        </div>
        <p className="text-center italic">
          Este documento foi gerado digitalmente pelo sistema MiaHealth
        </p>
      </div>
    </div>
  );
}
